import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigateQuery } from '@/hooks';
import { connect } from 'react-redux';
import { getCampaignTranslations } from '@/redux/campaign/selectors';
import { pushEvent } from '@CS/hooks';
import API from '@/services/api';
import { getQuizzes } from '@/redux/quizzes/selectors';
import { setQuizQueue } from '@/redux/quizzes/actions';
import type { CampaignQuiz } from '@/services/api/types';
import {
  createQuizQueue,
  DEFAULT_MATCH_QUESTIONS,
  type GameQuestion
} from '@/utils/quizQueue';

import styles from './IntroReady.module.css';
import { Slider} from '@/components/Slider';

type IntroReadyProps = {
  translations: Record<string, string>;
  quizzes: CampaignQuiz[];
  setQuizQueue: (queue: GameQuestion[]) => void;
};

const IntroReady = ({ translations, quizzes, setQuizQueue }: IntroReadyProps) => {
  const navigate = useNavigateQuery();
  const navigateRef = useRef(navigate);
  const isPreparingRef = useRef(false);
  const eventSent = useRef({ teletekst: false, trending: false });

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const [players, setPlayers] = useState(() => ({
    teletekst: { ready: false },
    trending: { ready: false }
  }));

  const toggleReady = useCallback((id: 'teletekst' | 'trending') => {
    setPlayers(prev => {
      if (prev[id].ready) return prev;
      return { ...prev, [id]: { ...prev[id], ready: true } };
    });
  }, []);

  const bothReady = players.teletekst.ready && players.trending.ready;

  useEffect(() => {
    if (!bothReady || isPreparingRef.current) return;
    isPreparingRef.current = true;

    const prepareAndNavigate = async () => {
      try {
        const sourceQuizzes =
          quizzes?.length ? quizzes : (await API.getCampaignQuizzes()).quizzes;

        setQuizQueue(createQuizQueue(sourceQuizzes, DEFAULT_MATCH_QUESTIONS));
      } catch (error) {
        console.error('Failed to prepare quiz queue:', error);
      }

      setTimeout(() => navigateRef.current('/game'), 800);
    };

    void prepareAndNavigate();
  }, [bothReady, quizzes, setQuizQueue]);

  const handleReady = (team: 'teletekst' | 'trending') => {
    if (eventSent.current[team]) return;
    
    eventSent.current[team] = true;
    
    pushEvent({
      event: 'button_click',
      eventAction: `/fcg26/starten_${team}`,
      eventLabel: '/fcg26/starten',
      team: team
    });
    
    toggleReady(team);
  };

  return (
    <div className={styles.container} style={{ touchAction: 'none' }}>
      <div className={`${styles.teamSection} ${styles.trendingTeam} ${styles.rotated}`}>
        <div className={styles.contentWrapper}>
          <h1 className={styles.teamTitle}>{translations.introReadygenTrendingReady}</h1>
          <div className={styles.buttonContainer}>
            <Slider
              key="trending"
              translations={translations}
              text={translations.introReadyslideToStart}
              inverted={true}
              onReady={() => {
                handleReady('trending');
              }}
              isSuccess={players.trending.ready ? true : undefined}
            />
          </div>
        </div>
      </div>
      <div className={`${styles.teamSection} ${styles.teletekstTeam}`}>
        <div className={styles.contentWrapper}>
          <h1 className={styles.teamTitle}>{translations.introReadygenTeletekstReady}</h1>
          <div className={styles.buttonContainer}>
            <Slider 
              key="teletekst"
              translations={translations}
              text={translations.introReadyslideToStart}
              inverted={false}
              onReady={() => {
                handleReady('teletekst');
              }}
              isSuccess={players.teletekst.ready ? true : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: never) => ({
  translations: getCampaignTranslations(state),
  quizzes: getQuizzes(state)
});

const mapDispatchToProps = {
  setQuizQueue
};

export default connect(mapStateToProps, mapDispatchToProps)(IntroReady);